import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2,
  Send,
  Calendar as CalendarIcon,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  PlayCircle,
  Trash2,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CITY_OPTIONS = ['International', 'London', 'Manchester', 'Chandigarh', 'Delhi', 'Toronto', 'Vancouver'];

export const AdminFBAutoPost = () => {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [status, setStatus] = useState(null);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState(false);
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);
  const [postsPerDay, setPostsPerDay] = useState(1);
  const [cities, setCities] = useState(['International', 'London', 'Chandigarh', 'Toronto']);
  const [statusFilter, setStatusFilter] = useState('queued');
  const [testMsg, setTestMsg] = useState('Shopperden test post — please ignore.');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API_URL}/api/admin/fb-autopost/status`, auth),
        axios.get(`${API_URL}/api/admin/fb-autopost/scheduled?status_filter=${statusFilter}&limit=100`, auth),
      ]);
      setStatus(s.data);
      setScheduled(l.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load FB Auto-Post status');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, statusFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleCity = (c) => {
    setCities((curr) =>
      curr.includes(c) ? curr.filter((x) => x !== c) : [...curr, c]
    );
  };

  const schedule = async () => {
    if (cities.length === 0) {
      toast.error('Pick at least one city');
      return;
    }
    if (!window.confirm(`Schedule the 30-day calendar at ${postsPerDay}/day rotating across ${cities.length} cities? Existing queued posts for the same calendar will be skipped.`)) return;
    setScheduling(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/fb-autopost/schedule`, {
        posts_per_day: postsPerDay,
        cities,
      }, auth);
      toast.success(`${r.data.scheduled} posts scheduled · ${r.data.skipped} skipped`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Schedule failed');
    } finally {
      setScheduling(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/fb-autopost/run-now?max_per_run=5`, {}, auth);
      if (r.data.error) {
        toast.error(r.data.error);
      } else {
        toast.success(`Posted ${r.data.posted} · Failed ${r.data.failed}`);
      }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const sendTest = async (dryRun) => {
    if (!(testMsg || '').trim()) {
      toast.error('Type a test message first');
      return;
    }
    setTesting(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/fb-autopost/test`, {
        message: testMsg,
        link: 'https://shopperden.com',
        dry_run: dryRun,
      }, auth);
      if (dryRun) {
        toast.success(`Dry-run OK — would post: "${(testMsg || '').slice(0, 60)}..."`);
      } else if (r.data.permalink) {
        toast.success('Posted live to Facebook!');
        window.open(r.data.permalink, '_blank');
      } else if (r.data.id || r.data.post_id) {
        toast.success('Posted live to Facebook!');
      } else {
        toast.success('Post submitted');
      }
    } catch (e) {
      const msg = e.response?.data?.detail?.error || e.response?.data?.detail || e.message || 'Test post failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setTesting(false);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancel this scheduled post?')) return;
    try {
      await axios.post(`${API_URL}/api/admin/fb-autopost/cancel/${id}`, {}, auth);
      toast.success('Post cancelled');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete ALL ${statusFilter} posts? This is irreversible.`)) return;
    try {
      const r = await axios.delete(`${API_URL}/api/admin/fb-autopost/scheduled/clear?status_filter=${statusFilter}`, auth);
      toast.success(`Deleted ${r.data.deleted}`);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clear failed');
    }
  };

  if (loading && !status) {
    return (
      <div className="pe-card text-center" style={{ padding: 'var(--pe-space-xl)' }}>
        <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{ color: 'var(--pe-gold)' }} />
      </div>
    );
  }

  const cred = status?.credentials || {};
  const credValid = cred.valid;
  const hasPagesScope = (cred.scopes || []).includes('pages_manage_posts');

  return (
    <div className="space-y-5" data-testid="admin-fb-autopost">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="pe-label mb-1">Facebook Auto-Poster</p>
          <h2 className="pe-h2">Page Auto-Scheduler</h2>
          <p className="pe-body mt-1" style={{ maxWidth: '640px' }}>
            Push your 30-day calendar live to the Shopperden Page automatically.
            Backed by Meta Graph API · 5-minute dispatcher · 3 retries on failure.
          </p>
        </div>
        <button type="button" onClick={loadAll} className="pe-btn pe-btn-ghost" data-testid="fb-autopost-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Credentials card */}
      <div className="pe-card" style={{ padding: 'var(--pe-space-md)' }} data-testid="fb-autopost-cred">
        <div className="flex items-center gap-2 mb-3">
          {credValid ? (
            <CheckCircle2 className="h-5 w-5" style={{ color: 'var(--pe-success)' }} />
          ) : (
            <XCircle className="h-5 w-5" style={{ color: 'var(--pe-error)' }} />
          )}
          <h3 className="pe-h3" style={{ fontSize: '1rem', margin: 0 }}>
            Credentials {credValid ? 'OK' : 'Missing or invalid'}
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <p className="pe-kpi-label">Page ID env</p>
            <p className="pe-kpi-value" style={{ color: cred.page_id_env ? 'var(--pe-success)' : 'var(--pe-error)' }}>
              {cred.page_id_env ? 'Set' : 'Missing'}
            </p>
          </div>
          <div>
            <p className="pe-kpi-label">Token env</p>
            <p className="pe-kpi-value" style={{ color: cred.page_token_env ? 'var(--pe-success)' : 'var(--pe-error)' }}>
              {cred.page_token_env ? 'Set' : 'Missing'}
            </p>
          </div>
          <div>
            <p className="pe-kpi-label">Page name</p>
            <p className="pe-kpi-value" style={{ fontSize: '0.95rem' }}>{cred.page_name || '—'}</p>
          </div>
          <div>
            <p className="pe-kpi-label">Token expires in</p>
            <p className="pe-kpi-value" style={{ fontSize: '0.95rem' }}>
              {cred.expires_in_days != null ? `${cred.expires_in_days} days` : '—'}
            </p>
          </div>
        </div>
        {cred.scopes && cred.scopes.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {cred.scopes.map((s) => (
              <span
                key={s}
                className={`pe-chip ${(s === 'pages_manage_posts' || s === 'pages_read_engagement' || s === 'pages_show_list') ? 'pe-chip-gold' : ''}`}
              >
                {s}
              </span>
            ))}
          </div>
        )}
        {!credValid && (
          <div className="mt-3 pe-surface-2" style={{ padding: '12px 14px', borderTop: '1px solid var(--pe-border)' }}>
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--pe-warning)' }} />
              <div className="pe-body" style={{ fontSize: '0.8125rem' }}>
                <b style={{ color: 'var(--pe-text)' }}>Setup steps:</b><br/>
                1. Open <a href="https://developers.facebook.com/tools/explorer/1170690548460576/" target="_blank" rel="noreferrer" style={{ color: 'var(--pe-gold)' }}>Graph API Explorer</a><br/>
                2. Add permissions: <code>pages_show_list</code> · <code>pages_read_engagement</code> · <code>pages_manage_posts</code><br/>
                3. Generate User Token → log in as Page admin → tick Shopperden Page → Allow<br/>
                4. Switch dropdown to <b>Page Token | Shopperden</b><br/>
                5. Paste the Page Token into <code>META_FB_PAGE_ACCESS_TOKEN</code> in <code>/app/backend/.env</code> + restart backend.<br/>
                {cred.error && <span style={{ color: 'var(--pe-error)' }}>Error: {cred.error}</span>}
              </div>
            </div>
          </div>
        )}
        {credValid && !hasPagesScope && (
          <div className="mt-3 pe-surface-2" style={{ padding: '12px 14px', borderTop: '1px solid var(--pe-warning)' }}>
            <div className="pe-body" style={{ fontSize: '0.8125rem' }}>
              <b style={{ color: 'var(--pe-warning)' }}>Heads up:</b> token works for read but missing <code>pages_manage_posts</code> — posting will fail. Re-generate with that scope.
            </div>
          </div>
        )}
      </div>

      {/* Test post card */}
      <div className="pe-card" style={{ padding: 'var(--pe-space-md)' }} data-testid="fb-autopost-test-card">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-5 w-5" style={{ color: 'var(--pe-gold)' }} />
          <h3 className="pe-h3" style={{ fontSize: '1rem', margin: 0 }}>Send a test post</h3>
        </div>
        <textarea
          className="w-full rounded border-2 px-3 py-2 text-sm bg-white text-slate-900 placeholder:text-slate-400 border-slate-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 focus:outline-none transition-colors resize-y"
          rows={3}
          value={testMsg}
          onChange={(e) => setTestMsg(e.target.value)}
          placeholder="Type a test message — e.g. &quot;Welcome to Shopperden — the AI Real Estate Network&quot;"
          data-testid="fb-autopost-test-msg"
          maxLength={2000}
        />
        <div className="text-[10px] text-slate-400 mt-1">{(testMsg || '').length}/2000 chars</div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            onClick={() => sendTest(true)}
            disabled={testing}
            className="pe-btn pe-btn-ghost"
            data-testid="fb-autopost-test-dry"
          >
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Dry-run
          </button>
          <button
            type="button"
            onClick={() => sendTest(false)}
            disabled={testing || !credValid}
            className="pe-btn pe-btn-primary"
            data-testid="fb-autopost-test-live"
            title={credValid ? 'Posts to your real FB Page' : 'Configure Page Token first'}
          >
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Post live
          </button>
        </div>
      </div>

      {/* Schedule controls */}
      <div className="pe-card" style={{ padding: 'var(--pe-space-md)' }} data-testid="fb-autopost-schedule-card">
        <div className="flex items-center gap-2 mb-3">
          <CalendarIcon className="h-5 w-5" style={{ color: 'var(--pe-gold)' }} />
          <h3 className="pe-h3" style={{ fontSize: '1rem', margin: 0 }}>Schedule the 30-day calendar</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="pe-kpi-label mb-2">Posts per day</p>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPostsPerDay(n)}
                  className={postsPerDay === n ? 'pe-btn pe-btn-primary' : 'pe-btn pe-btn-ghost'}
                  data-testid={`fb-autopost-ppd-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="pe-kpi-label mb-2">City rotation ({cities.length} selected)</p>
            <div className="flex flex-wrap gap-1.5">
              {CITY_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCity(c)}
                  className={cities.includes(c) ? 'pe-btn pe-btn-primary' : 'pe-btn pe-btn-ghost'}
                  style={{ fontSize: '0.7rem', padding: '0.3rem 0.6rem' }}
                  data-testid={`fb-autopost-city-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            type="button"
            onClick={schedule}
            disabled={scheduling}
            className="pe-btn pe-btn-primary"
            data-testid="fb-autopost-schedule-btn"
          >
            {scheduling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarIcon className="h-3.5 w-3.5" />} Schedule calendar
          </button>
          <button
            type="button"
            onClick={runNow}
            disabled={running}
            className="pe-btn pe-btn-ghost"
            data-testid="fb-autopost-run-now"
            title="Manually dispatch up to 5 due posts immediately"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />} Run dispatcher now
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="pe-card flex flex-wrap items-center gap-4" style={{ padding: 'var(--pe-space-md)' }} data-testid="fb-autopost-kpi">
        <div className="flex-1 min-w-[160px]">
          <p className="pe-kpi-label">Queued</p>
          <p className="pe-kpi-value pe-kpi-value-gold">{status?.queued ?? 0}</p>
        </div>
        <div className="flex-1 min-w-[160px]">
          <p className="pe-kpi-label">Posted</p>
          <p className="pe-kpi-value" style={{ color: 'var(--pe-success)' }}>{status?.posted ?? 0}</p>
        </div>
        <div className="flex-1 min-w-[160px]">
          <p className="pe-kpi-label">Failed</p>
          <p className="pe-kpi-value" style={{ color: 'var(--pe-error)' }}>{status?.failed ?? 0}</p>
        </div>
        <div className="flex-1 min-w-[220px]">
          <p className="pe-kpi-label">Next scheduled</p>
          <p className="pe-body" style={{ color: 'var(--pe-text)', fontWeight: 600 }}>
            {status?.next_scheduled?.scheduled_at
              ? new Date(status.next_scheduled.scheduled_at).toLocaleString()
              : '—'}
          </p>
        </div>
      </div>

      {/* Scheduled list */}
      <div className="pe-card" style={{ padding: 'var(--pe-space-md)' }} data-testid="fb-autopost-list">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            {['queued', 'posted', 'failed', 'cancelled'].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                className={statusFilter === s ? 'pe-btn pe-btn-primary' : 'pe-btn pe-btn-ghost'}
                style={{ padding: '0.3rem 0.7rem', fontSize: '0.75rem' }}
                data-testid={`fb-autopost-filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
          {(statusFilter === 'queued' || statusFilter === 'failed' || statusFilter === 'cancelled') && scheduled.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="pe-btn pe-btn-ghost"
              style={{ color: 'var(--pe-error)' }}
              data-testid="fb-autopost-clear-all"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete all ({scheduled.length})
            </button>
          )}
        </div>
        {scheduled.length === 0 ? (
          <p className="pe-body" style={{ color: 'var(--pe-text-muted)', textAlign: 'center', padding: 'var(--pe-space-md)' }}>
            No <b>{statusFilter}</b> posts.
          </p>
        ) : (
          <div className="space-y-2">
            {scheduled.slice(0, 50).map((p) => (
              <div
                key={p._id}
                className="pe-surface-2"
                style={{ padding: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}
                data-testid={`fb-autopost-row-${p._id}`}
              >
                <div style={{ flex: '1 1 60%', minWidth: '240px' }}>
                  <p className="pe-body" style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    {p.title || '(untitled)'}
                  </p>
                  <p className="pe-body" style={{ fontSize: '0.75rem', color: 'var(--pe-text-muted)' }}>
                    <span className="pe-chip pe-chip-gold" style={{ marginRight: '6px' }}>{p.category_id}</span>
                    <span>{p.city}</span>
                    {' · '}
                    <span>{p.scheduled_at ? new Date(p.scheduled_at).toLocaleString() : ''}</span>
                  </p>
                  {p.permalink && (
                    <a href={p.permalink} target="_blank" rel="noreferrer" className="pe-body" style={{ fontSize: '0.7rem', color: 'var(--pe-gold)' }}>
                      View on Facebook ↗
                    </a>
                  )}
                  {p.error && (
                    <p className="pe-body" style={{ fontSize: '0.7rem', color: 'var(--pe-error)' }}>{p.error}</p>
                  )}
                </div>
                {p.status === 'queued' && (
                  <button
                    type="button"
                    onClick={() => cancel(p._id)}
                    className="pe-btn pe-btn-ghost"
                    style={{ padding: '0.25rem 0.6rem', fontSize: '0.7rem' }}
                    data-testid={`fb-autopost-cancel-${p._id}`}
                  >
                    Cancel
                  </button>
                )}
              </div>
            ))}
            {scheduled.length > 50 && (
              <p className="pe-body" style={{ fontSize: '0.75rem', color: 'var(--pe-text-muted)', textAlign: 'center' }}>
                Showing 50 of {scheduled.length}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFBAutoPost;
